// packages/core/src/learning/schools/options-derivatives.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'nassim-taleb' and
// 'jim-simons' (both valid FigureIds) — wire into `figures` during the
// cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const OPTIONS_DERIVATIVES_EXHIBIT: SchoolExhibitEntry = {
	id: 'options-derivatives',
	tagline:
		'Options and derivatives believe risk can be shaped, traded, and multiplied.',
	worldviewSummary:
		'Options and derivatives is paying attention to contracts, leverage, hedging, payoff shapes, and how risk changes under different conditions. This lens protects you from thinking all market exposure behaves the same way. It accepts a serious tradeoff: contracts can expire, leverage can magnify losses, pricing can move fast, and complexity can hide risk from the person taking it. The blind spot is control illusion. A strategy can look precise on paper while still creating losses, forced decisions, and outcomes the user did not fully understand.',
	coreBeliefs: [
		'Options and derivatives believes market exposure can be shaped through contracts instead of only direct ownership.',
		'Options and derivatives believes risk is not one thing. Direction, time, volatility, liquidity, and leverage can all matter.',
		'Options and derivatives believes hedging can reduce one risk while introducing another.',
		'Options and derivatives believes payoff shape matters because the same market move can affect different contracts differently.',
		'Options and derivatives believes leverage is powerful, but losses are possible and can arrive faster than expected.',
		'Options and derivatives believes income strategies are not free money. They trade one risk for another.',
		'Options and derivatives believes complexity should be respected before any strategy is trusted.',
	],
	warnsAgainst: [
		'Treating options like simple stock substitutes.',
		'Calling a strategy safe because the payoff diagram looks clean.',
		'Ignoring expiration, volatility, liquidity, assignment, and margin risk.',
		'Believing premium income is dependable without understanding what risk is being sold.',
	],
	helps: 'This lens helps people understand why contracts, hedging, leverage, and payoff shapes change how market risk behaves. It may not fit someone who needs simplicity, low attention investing, emotional calm, or strong protection from fast losses.',
	figures: [],
	canon: [
		{
			title: 'Black Scholes model',
			oneLineIdea:
				'Options pricing depends on more than direction. Time, volatility, interest rates, and strike price all matter.',
		},
		{
			title: 'The Greeks',
			oneLineIdea:
				'Options risk can be broken into sensitivities like price movement, time decay, and volatility changes.',
		},
		{
			title: 'Dynamic hedging',
			oneLineIdea:
				'A hedge is not a set and forget shield. It can require adjustment as prices and risk change.',
		},
		{
			title: 'Asymmetric payoff',
			oneLineIdea:
				'Some contracts create uneven outcomes where upside, downside, and probability do not move in a straight line.',
		},
		{
			title: 'Tail risk',
			oneLineIdea:
				'Rare events can matter more than they look, especially when leverage or hidden exposure is involved.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-options-derivatives',
	debateIds: [],
	applyActions: [
		{
			label: 'Check whether your runway can absorb fast losses',
			action: { type: 'calculateRunway', label: 'Check your runway' },
		},
		{
			label: 'Compare a complex strategy against your real money picture',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Test how a risky outcome changes your plan',
			action: {
				type: 'openSimulator',
				label: 'Simulate a risk case',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write the loss case before the payoff story',
			action: {
				type: 'noToolAction',
				label: 'Write the loss case',
				prompt: 'Write how an options strategy could lose money before writing how it could work.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Options are not magic income. They are contracts that move risk around.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of leverage is that mistakes arrive faster.',
			kind: 'hook',
		},
		{
			line: 'What risk do beginners miss when they hear “collect premium”?',
			kind: 'interview-question',
		},
		{
			line: 'When does hedging protect you, and when does it just make risk harder to see?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches options as risk shape, not as a promise of easy income.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Users should understand contracts, leverage, and loss paths before they trust a payoff diagram.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how a strategy can look calm until volatility changes the whole picture.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone to explain the worst case before they explain the upside.',
			kind: 'creator-prompt',
		},
		{
			line: 'Walk through why selling risk can feel like income until the wrong day arrives.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
